import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Unlock } from 'lucide-react';
import toast from 'react-hot-toast';
import type { EnrichedToothImage, OverlayTransform } from '@/hooks/useToothImages';
import { useUpdateToothImageOverlay, buildCssFilter } from '@/hooks/useToothImages';
import { OpacitySlider } from './OpacitySlider';
import { useDentalOverlay } from './DentalOverlayContext';

interface ImageOverlayLayerProps {
  image: EnrichedToothImage;
  patientId: string;
}

/**
 * Absolutely positioned image overlay rendered over the ToothChartStage.
 * Supports drag-to-move and ctrl+drag to scale. Persists to overlay_transform.
 */
export function ImageOverlayLayer({ image, patientId }: ImageOverlayLayerProps) {
  const { t } = useTranslation();
  const { opacity, setOpacity, setPinnedImage } = useDentalOverlay();
  const updateOverlay = useUpdateToothImageOverlay();

  const initialTransform: OverlayTransform = image.overlay_transform ?? {
    x: 0, y: 0, scale: 0.5, rotation: 0, opacity: 0.5,
  };

  const [transform, setTransform] = useState<OverlayTransform>(initialTransform);
  const [locked, setLocked] = useState(false);

  const dragging = useRef(false);
  const dragStart = useRef({ mx: 0, my: 0, tx: 0, ty: 0 });
  const ctrlDragging = useRef(false);
  const ctrlDragStart = useRef({ my: 0, startScale: 1 });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (locked) return;
      e.preventDefault();
      e.stopPropagation();

      if (e.ctrlKey || e.metaKey) {
        ctrlDragging.current = true;
        ctrlDragStart.current = { my: e.clientY, startScale: transform.scale };
      } else {
        dragging.current = true;
        dragStart.current = { mx: e.clientX, my: e.clientY, tx: transform.x, ty: transform.y };
      }
    },
    [locked, transform.scale, transform.x, transform.y],
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (dragging.current) {
        setTransform((prev) => ({
          ...prev,
          x: dragStart.current.tx + (e.clientX - dragStart.current.mx),
          y: dragStart.current.ty + (e.clientY - dragStart.current.my),
        }));
      } else if (ctrlDragging.current) {
        const dy = e.clientY - ctrlDragStart.current.my;
        const newScale = Math.max(0.1, ctrlDragStart.current.startScale - dy * 0.005);
        setTransform((prev) => ({ ...prev, scale: newScale }));
      }
    };

    const handleMouseUp = () => {
      dragging.current = false;
      ctrlDragging.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleLockToggle = async () => {
    const newLocked = !locked;
    setLocked(newLocked);
    if (newLocked) {
      try {
        await updateOverlay.mutateAsync({
          imageId: image.id,
          patientId,
          overlayTransform: { ...transform, opacity },
        });
      } catch {
        toast.error(t('common.error'));
      }
    }
  };

  const handleClose = async () => {
    try {
      await updateOverlay.mutateAsync({
        imageId: image.id,
        patientId,
        overlayTransform: { ...transform, opacity },
      });
    } catch {
      // Non-critical
    }
    setPinnedImage(null);
  };

  const cssFilter = buildCssFilter(image.adjustments ?? {});

  return (
    <div className="absolute inset-0 z-10 pointer-events-none">
      {/* Draggable image */}
      <div
        className="absolute pointer-events-auto"
        style={{
          left: `calc(50% + ${transform.x}px)`,
          top: `calc(50% + ${transform.y}px)`,
          transform: `translate(-50%, -50%) scale(${transform.scale})`,
          opacity,
          cursor: locked ? 'default' : 'move',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
      >
        <img
          src={image.signedUrl ?? ''}
          alt={image.document_original_filename ?? ''}
          style={{ filter: cssFilter, maxWidth: '100%', pointerEvents: 'none' }}
          draggable={false}
        />

        {/* Mini-toolbar */}
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-lg bg-black/70 px-2 py-1 pointer-events-auto">
          <button
            onClick={handleLockToggle}
            title={locked ? t('dental.imaging.overlay.lockPosition') : t('dental.imaging.overlay.lockPosition')}
            className={`rounded p-1 text-xs ${locked ? 'text-amber-400' : 'text-ds-placeholder hover:text-white'}`}
          >
            {locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
          </button>
          <span className="text-xs text-ds-muted">{Math.round(transform.scale * 100)}%</span>
        </div>
      </div>

      {/* Opacity slider — pointer events enabled */}
      <div className="pointer-events-auto">
        <OpacitySlider opacity={opacity} onChange={setOpacity} onClose={handleClose} />
      </div>
    </div>
  );
}
